import React, { useState } from "react";
import Dropdown from "components/dropdown";
import { FiAlignJustify } from "react-icons/fi";
import { FiSearch } from "react-icons/fi";
import { RiMoonFill, RiSunFill } from "react-icons/ri";
import avatar from "assets/img/avatars/avatar4.png";
import { useDispatch, useSelector } from "react-redux";
import { setRefreshToken, setToken } from "redux/reducers/authReducer";
import { setSideBarState } from "../../redux/reducers/sideBarReducer";
import { useTranslation } from "react-i18next";
import i18n from "i18n";
import languageIcon from "../../assets/svg/languageIcon.svg";
import SwitchButton from "components/toggleSwitch";
import { UIService, UITypes } from "front-lib";
import { RootState } from "redux/store";

type NavbarProps = {
  onOpenSidenav?: () => void;
  brandText?: string;
  secondary?: boolean | string;
  handleSearch?: (e: React.SyntheticEvent<EventTarget>) => void;
  setSearchText?: (val: string) => void;
  flag?: boolean;
};

const Navbar = (props: NavbarProps) => {
  const { handleSearch, setSearchText, flag, brandText } = props;
  const [darkmode, setDarkmode] = React.useState(false);

  React.useEffect(() => {
    const saved = localStorage.getItem("theme");

    if (saved === "dark") {
      document.documentElement.classList.add("dark");
      setDarkmode(true);
    } else {
      document.documentElement.classList.remove("dark");
      setDarkmode(false);
    }
  }, []);

  const sidebarFlag = useSelector(
    (store: RootState) => store.sideBarStateChange.sideBarState
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const [isOpen, setIsOpen] = useState(false);

  const toggleDropdown = () => {
    setIsOpen(!isOpen);
  };

  const chooseLanguage = (language: string) => {
    i18n.changeLanguage(language);
    localStorage.setItem("lang", language);
    setIsOpen(false);
  };

  const handleLogout = () => {
    dispatch(setToken(null));
    dispatch(setRefreshToken(null));
  };

  UIService.eventManager.on(
    UITypes.AppEventNames.LOGOUT,
    handleLogout.bind(this)
  );

  return (
    <nav className="sticky top-4 z-40 flex flex-row flex-wrap items-center justify-between rounded-2xl bg-[#18181F]/60 backdrop-blur-xl border border-white/5 p-3 mb-4">
      {/* Brand text / Title */}
      <div className="flex items-center gap-4">
        <span
          className="flex cursor-pointer text-xl text-gray-400 hover:text-cyan-400 transition-colors xl:hidden"
          onClick={() => dispatch(setSideBarState(!sidebarFlag))}
        >
          <FiAlignJustify className="h-6 w-6" />
        </span>
        {brandText && (
          <h1 className="text-xl font-semibold text-white">{brandText}</h1>
        )}
      </div>

      {/* Right side controls */}
      <div className="flex items-center gap-3">
        <SwitchButton />

        {/* Language selector */}
        <div className="relative">
          <button
            className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
            onClick={toggleDropdown}
          >
            <img src={languageIcon} alt="Language" className="h-5 w-5" />
          </button>
          {isOpen && (
            <div className="absolute right-0 mt-2 w-36 rounded-xl border border-white/10 bg-[#18181F] shadow-xl z-50">
              <button
                className="block w-full px-4 py-3 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-cyan-400 transition-colors rounded-t-xl"
                onClick={() => chooseLanguage("en")}
              >
                English
              </button>
              <button
                className="block w-full px-4 py-3 text-left text-sm text-gray-300 hover:bg-white/5 hover:text-cyan-400 transition-colors rounded-b-xl"
                onClick={() => chooseLanguage("pt")}
              >
                Português
              </button>
            </div>
          )}
        </div>

        {/* Search bar - only if flag is true */}
        {flag && (
          <form onSubmit={handleSearch} className="hidden md:block">
            <div className="flex h-10 items-center rounded-xl bg-white/5 px-3">
              <FiSearch className="h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Search..."
                className="ml-2 h-full w-48 bg-transparent text-sm text-white outline-none placeholder:text-gray-500"
                onChange={(e) => setSearchText?.(e.target.value)}
              />
            </div>
          </form>
        )}

        {/* Dark mode toggle */}
        <button
          className="flex h-10 w-10 items-center justify-center rounded-xl bg-white/5 hover:bg-white/10 transition-colors"
          onClick={() => {
            if (darkmode) {
              document.documentElement.classList.remove("dark");
              localStorage.setItem("theme", "light");
              setDarkmode(false);
            } else {
              document.documentElement.classList.add("dark");
              localStorage.setItem("theme", "dark");
              setDarkmode(true);
            }
          }}
        >
          {darkmode ? (
            <RiSunFill className="h-5 w-5 text-amber-400" />
          ) : (
            <RiMoonFill className="h-5 w-5 text-gray-400" />
          )}
        </button>

        {/* Profile & Dropdown */}
        <Dropdown
          button={
            <img
              className="h-10 w-10 cursor-pointer rounded-full ring-2 ring-cyan-500/30"
              src={avatar}
              alt="Profile"
            />
          }
          children={
            <div className="flex flex-col rounded-xl bg-[#18181F] border border-white/10 shadow-xl p-4 min-w-[160px]">
              <a
                onClick={handleLogout}
                href="/login"
                className="text-sm font-medium text-red-400 hover:text-red-300 transition-colors"
              >
                {t("Log Out")}
              </a>
            </div>
          }
          classNames={"py-2 top-12 -left-[120px] w-max"}
        />
      </div>
    </nav>
  );
};

export default Navbar;

